import api from './api'
import { saveBlob } from '../../lib/download'

/**
 * The RESELLER COMPANY file manager (spec §3.3 / §5.1) — the cabinet holding
 * what a reseller signed in its portal and what XOP filed against it.
 *
 * Same endpoint SHAPE as the customer and prospect file managers, because
 * crm-api serves all three from one concern (`EntityAttachments`). The one row
 * field that matters here and does not on a customer is `deletable`: both XOP
 * and the reseller write into this list, and only the side that uploaded a file
 * may remove it. The flag is computed server-side from the same predicate
 * DELETE enforces, so the button and the endpoint cannot disagree — never
 * re-derive it in the client.
 */
export interface ResellerAttachment {
  id: string
  name: string
  original_filename: string
  content_type: string
  file_size: number
  size: number
  type: string
  created_at: string
  /** `"staff"` or `"reseller"` — which side of the relationship filed it. */
  uploaded_by_kind: 'staff' | 'reseller' | string
  uploaded_by_email?: string | null
  /** Server's verdict on whether THIS viewer may delete THIS file. */
  deletable: boolean
  uploaded_by: { id: string | null; name: string }
}

export interface ResellerAttachmentsListResponse {
  success: boolean
  attachments: ResellerAttachment[]
  pagination?: {
    current_page: number
    per_page: number
    total_count: number
    total_pages: number
  }
  error?: string
}

export interface ResellerAttachmentUploadResponse {
  success: boolean
  attachment?: ResellerAttachment
  message?: string
  error?: string
}

class ResellerAttachmentsApi {
  async list(
    resellerId: string,
    params?: { page?: number; per_page?: number; search?: string },
  ): Promise<ResellerAttachmentsListResponse> {
    const query = new URLSearchParams()
    if (params?.page) query.append('page', String(params.page))
    if (params?.per_page) query.append('per_page', String(params.per_page))
    if (params?.search) query.append('search', params.search)

    const suffix = query.toString() ? `?${query.toString()}` : ''
    const response = await api.get(`/resellers/${resellerId}/attachments${suffix}`)
    return response.data
  }

  async upload(resellerId: string, file: File): Promise<ResellerAttachmentUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post(`/resellers/${resellerId}/attachments/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async download(resellerId: string, attachmentId: string, filename?: string): Promise<void> {
    const response = await api.get(
      `/resellers/${resellerId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' },
    )
    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename || `attachment-${attachmentId}`, new Blob([response.data]))
  }

  /**
   * Raw bytes for the shared `<PdfReader>` — never a `blob:` iframe, which
   * hangs the macOS desktop shell.
   *
   * `.slice(0)` is a COPY and is the whole point. pdf.js DETACHES the
   * ArrayBuffer it is handed, so handing it a buffer anything else still
   * references makes the SECOND preview of the same file throw "Buffer is
   * already detached" — a crash this app has shipped once already. Note that
   * `new Uint8Array(buffer)` does NOT copy: it is a view onto the same buffer,
   * and detaching that buffer detaches the view with it.
   */
  async bytes(resellerId: string, attachmentId: string): Promise<ArrayBuffer> {
    const response = await api.get(
      `/resellers/${resellerId}/attachments/${attachmentId}/view`,
      { responseType: 'arraybuffer' },
    )
    return (response.data as ArrayBuffer).slice(0)
  }

  async remove(resellerId: string, attachmentId: string): Promise<{ success: boolean; error?: string }> {
    const response = await api.delete(`/resellers/${resellerId}/attachments/${attachmentId}`)
    return response.data
  }
}

export const resellerAttachmentsApi = new ResellerAttachmentsApi()
export default resellerAttachmentsApi
